import {
  FOCUS_FIRST_PLACEHOLDER_COMMAND,
  REPLACE_TEXT_COMMAND,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $isElementNode, CLEAR_EDITOR_COMMAND } from "lexical";
import { useCallback, useEffect, useState } from "react";

type SetMessageFunction = (
  value: string | ((prevValue: string) => string),
) => void;

type FocusPlaceholderFunction = () => void;
type GetTransformedMessageFunction = () => string;

export function useLexicalMessageAbstraction(): {
  message: string;
  setMessage: SetMessageFunction;
  focusPlaceholder: FocusPlaceholderFunction;
  getTransformedMessage: GetTransformedMessageFunction;
} {
  const [editor] = useLexicalComposerContext();
  const [message, setLocalMessage] = useState("");

  // Subscribe to editor changes
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        setLocalMessage(root.getTextContent());
      });
    });
  }, [editor]);

  const setMessage: SetMessageFunction = useCallback(
    (value) => {
      const newValue = typeof value === "function" ? value(message) : value;

      if (newValue === "") {
        editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      } else {
        editor.dispatchCommand(REPLACE_TEXT_COMMAND, { text: newValue });
      }
    },
    [editor, message],
  );

  const focusPlaceholder = useCallback(() => {
    editor.dispatchCommand(FOCUS_FIRST_PLACEHOLDER_COMMAND, undefined);
  }, [editor]);

  const getTransformedMessage = useCallback(() => {
    let transformedText = "";

    editor.getEditorState().read(() => {
      const root = $getRoot();

      const processNode = (node: any): string => {
        if ($isElementNode(node)) {
          const children = node.getChildren();
          return children.map(processNode).join("");
        }
        return node.getTextContent?.() || "";
      };

      transformedText = processNode(root);
    });

    return transformedText;
  }, [editor]);

  return { message, setMessage, focusPlaceholder, getTransformedMessage };
}
